import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getCodeById, 
  toggleFavorite, 
  downloadFilesAsZip, 
  downloadSingleFile,
  saveCode 
} from '../../../services/codegen.service';
import { showSuccess, showError, showInfo, showWarning } from '../../../services/notification.service';
import './CodeViewer.css';

const CodeViewer = () => {
  const { codeId } = useParams();
  const navigate = useNavigate();
  const [codeData, setCodeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCodeId, setSavedCodeId] = useState(null); // Store the actual saved code ID from code_files collection

  useEffect(() => {
    loadCodeData();
  }, [codeId]);

  const loadCodeData = async () => {
    try {
      setLoading(true);
      
      // Try loading from database first
      try {
        const data = await getCodeById(codeId);
        
        if (data.success && data.code) {
          setCodeData(data.code);
          setIsSaved(true); // Mark as saved (from database)
          if (data.code.files && data.code.files.length > 0) {
            setSelectedFile(data.code.files[0]);
          }
          setLoading(false);
          return;
        }
      } catch (dbErr) {
        // If not in database, try session storage
        console.log('Not in database, checking session storage');
      }
      
      // If not in database, check session storage (for unsaved job results)
      const sessionData = sessionStorage.getItem(`jobCode_${codeId}`);
      if (sessionData) {
        try {
          const parsedData = JSON.parse(sessionData);
          // Session storage has nested structure: {code: {...}, jobId: ..., savedToHistory: bool, codeId: ...}
          const actualCodeData = parsedData.code || parsedData;
          setCodeData(actualCodeData);
          // Check if this job was already saved to history
          const isAlreadySaved = parsedData.savedToHistory || false;
          setIsSaved(isAlreadySaved);
          // Store the saved code ID if it exists (from code_files collection)
          if (parsedData.codeId) {
            setSavedCodeId(parsedData.codeId);
          }
          if (actualCodeData.files && actualCodeData.files.length > 0) {
            setSelectedFile(actualCodeData.files[0]);
          }
          setLoading(false);
          return;
        } catch (err) {
          console.error('Error parsing session data:', err);
        }
      }
      
      // If neither source found, show error
      setError('Code not found');
    } catch (err) {
      setError('Error loading code');
      console.error('Error loading code:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToHistory = async (skipNavigation = false) => {
    if (!codeData || saving) return null;
    
    try {
      setSaving(true);
      
      // Get job_id from session storage if available
      const sessionData = sessionStorage.getItem(`jobCode_${codeId}`);
      let jobId = null;
      if (sessionData) {
        try {
          const parsedData = JSON.parse(sessionData);
          jobId = parsedData.jobId;
        } catch (err) {
          console.error('Error parsing session data for job_id:', err);
        }
      }
      
      const response = await saveCode({
        title: codeData.title || `${capitalize(codeData.language)} Code`,
        requirements: codeData.requirements || '',
        language: codeData.language,
        framework: codeData.framework,
        files: codeData.files,
        job_id: jobId
      });
      
      if (response.success) {
        showSuccess('Code saved to history!');
        setIsSaved(true);
        // Clear ALL session storage for this job to prevent duplicates
        sessionStorage.removeItem(`jobCode_${codeId}`);
        // Update codeData with new ID
        setCodeData({...codeData, _id: response.code_id, is_favorite: false});
        
        // Only navigate if not skipping (i.e., not being called from favorite toggle)
        if (!skipNavigation) {
          // Navigate to the saved code URL
          navigate(`/tools/code-generator/codes/${response.code_id}`, { replace: true });
        }
        
        // Return the new code_id for use by caller
        return response.code_id;
      } else {
        showError('Failed to save code');
        return null;
      }
    } catch (err) {
      showError('Error saving code');
      console.error('Error saving code:', err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!codeData) return;
    
    // If already saved, we have the saved code ID from code_files collection
    if (isSaved && savedCodeId) {
      // Use the savedCodeId from code_files collection for favoriting
      try {
        const data = await toggleFavorite(savedCodeId, !codeData.is_favorite);
        
        if (data.success) {
          setCodeData({ ...codeData, is_favorite: !codeData.is_favorite });
          showSuccess(!codeData.is_favorite ? 'Added to favorites' : 'Removed from favorites');
        } else {
          showError('Failed to update favorite status');
        }
      } catch (err) {
        showError('Error updating favorite status');
        console.error('Error updating favorite:', err);
      }
      return;
    }
    
    // If not saved yet, save first then toggle favorite
    if (!isSaved) {
      showInfo('Saving to history first...');
      // Skip navigation so we can complete the favorite toggle
      const newCodeId = await handleSaveToHistory(true);
      
      if (!newCodeId) {
        showError('Failed to save code before favoriting');
        return;
      }
      
      // Store the new code ID
      setSavedCodeId(newCodeId);
      setIsSaved(true);
      
      // Now toggle favorite using the newly saved code ID
      try {
        const data = await toggleFavorite(newCodeId, true);
        if (data.success) {
          setCodeData(prev => ({ ...prev, _id: newCodeId, is_favorite: true }));
          showSuccess('Added to favorites');
          // Now navigate to the saved code URL
          navigate(`/tools/code-generator/codes/${newCodeId}`, { replace: true });
        } else {
          showError('Failed to update favorite status');
        }
      } catch (err) {
        showError('Error updating favorite status');
        console.error('Error updating favorite:', err);
      }
      return;
    }
    
    // Fallback: Code is saved but no savedCodeId (loaded from database directly)
    try {
      const data = await toggleFavorite(codeData._id || codeId, !codeData.is_favorite);
      
      if (data.success) {
        setCodeData({ ...codeData, is_favorite: !codeData.is_favorite });
        showSuccess(!codeData.is_favorite ? 'Added to favorites' : 'Removed from favorites');
      } else {
        showError('Failed to update favorite status');
      }
    } catch (err) {
      showError('Error updating favorite status');
      console.error('Error updating favorite:', err);
    }
  };

  const handleDownloadAll = async () => {
    if (!codeData || !codeData.files) return;
    
    try {
      showInfo('Preparing download...');
      await downloadFilesAsZip(codeData.files, codeData.language || 'code');
      showSuccess('Download started');
    } catch (err) {
      showError('Error downloading files');
      console.error('Error downloading files:', err);
    }
  };

  const handleDownloadFile = async (file) => {
    if (!file) return;
    
    try {
      downloadSingleFile(file);
      showSuccess(`Downloaded ${file.name || file.filename}`);
    } catch (err) {
      showError(`Error downloading ${file.name || file.filename}`);
      console.error('Error downloading file:', err);
    }
  };

  const capitalize = (str) => {
    if (!str) return '';
    // Special cases
    const specialCases = {
      'python': 'Python',
      'typescript': 'TypeScript',
      'javascript': 'JavaScript',
      'java': 'Java',
      'csharp': 'C#',
      'go': 'Go'
    };
    return specialCases[str.toLowerCase()] || str.charAt(0).toUpperCase() + str.slice(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="code-viewer-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading code...</p>
        </div>
      </div>
    );
  }

  if (error || !codeData) {
    return (
      <div className="code-viewer-container">
        <div className="error-container">
          <p>⚠️ {error || 'Code not found'}</p>
          <button className="btn-back" onClick={() => {
            const fromJobs = sessionStorage.getItem(`jobCode_${codeId}`);
            navigate(fromJobs ? '/tools/code-generator/jobs' : '/tools/code-generator/history');
          }}>
            ← Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="code-viewer-container">
      <div className="code-viewer-header">
        <div className="header-left">
          <button className="btn-back-icon" onClick={() => {
            // If session storage exists, we came from jobs page
            const fromJobs = sessionStorage.getItem(`jobCode_${codeId}`);
            navigate(fromJobs ? '/tools/code-generator/jobs' : '/tools/code-generator/history');
          }}>
            ← Back
          </button>
          <h2>{codeData.title || `${capitalize(codeData.language) || 'Code'} Generation`}</h2>
        </div>
        <div className="header-actions">
          {!isSaved && (
            <button
              className="btn-save-history"
              onClick={handleSaveToHistory}
              disabled={saving}
            >
              {saving ? '💾 Saving...' : '💾 Save to History'}
            </button>
          )}
          {isSaved && (
            <button
              className={`btn-favorite-header ${codeData.is_favorite ? 'favorited' : ''}`}
              onClick={handleToggleFavorite}
            >
              {codeData.is_favorite ? '⭐ Favorited' : '☆ Add to Favorites'}
            </button>
          )}
          <button className="btn-download-all" onClick={handleDownloadAll}>
            📥 Download All
          </button>
        </div>
      </div>

      {/* Top Row - Metadata & Requirements */}
      <div className="top-row">
        <div className="code-metadata">
          <div className="metadata-item">
            <span className="metadata-label">Language:</span>
            <span className="metadata-value">{capitalize(codeData.language) || 'N/A'}</span>
          </div>
          {codeData.framework && (
            <div className="metadata-item">
              <span className="metadata-label">Framework:</span>
              <span className="metadata-value">{codeData.framework}</span>
            </div>
          )}
          <div className="metadata-item">
            <span className="metadata-label">Generated:</span>
            <span className="metadata-value">{formatDate(codeData.created_at)}</span>
          </div>
          <div className="metadata-item">
            <span className="metadata-label">Files:</span>
            <span className="metadata-value">{codeData.files?.length || 0}</span>
          </div>
        </div>

        {codeData.requirements && (
          <div className="requirements-section">
            <h3>📋 Requirements</h3>
            <p>{codeData.requirements}</p>
          </div>
        )}
      </div>

      {/* Bottom Row - Files & Code Content */}
      {codeData.files && codeData.files.length > 0 && (
        <div className="bottom-row">
          <div className="files-sidebar">
            <h3>📁 Files</h3>
            <div className="files-list">
              {codeData.files.map((file, index) => (
                <div
                  key={index}
                  className={`file-item ${selectedFile === file ? 'selected' : ''}`}
                  onClick={() => setSelectedFile(file)}
                >
                  <span className="file-icon">📄</span>
                  <span className="file-name">{file.name || file.filename}</span>
                  <button
                    className="btn-download-file"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadFile(file);
                    }}
                    title="Download file"
                  >
                    ⬇️
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="file-content">
            {selectedFile ? (
              <>
                <div className="file-content-header">
                  <h3>{selectedFile.name || selectedFile.filename}</h3>
                  <button
                    className="btn-copy-code"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedFile.content || selectedFile.code);
                      showSuccess('Code copied to clipboard');
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
                <pre className="code-block">
                  <code>{selectedFile.content || selectedFile.code}</code>
                </pre>
              </>
            ) : (
              <div className="no-file-selected">
                <p>Select a file to view its content</p>
              </div>
            )}
          </div>
        </div>
      )}

      {(!codeData.files || codeData.files.length === 0) && (
        <div className="no-files">
          <p>📭 No files available</p>
        </div>
      )}
    </div>
  );
};

export default CodeViewer;
